import { Link } from "react-router-dom";
import { Linkedin, Youtube, Twitter, Facebook, Instagram, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import tangoLogo from "@/assets/brand/TangoNetworks_Horizontal_White.svg";

/* ── Tiny "Recognised by" line shown above the copyright row ── */
function RecognisedByLine() {
  const { data: orgs = [] } = useQuery({
    queryKey: ["awards-footer-orgs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("awards" as any)
        .select("organisation, sort_order")
        .eq("published", true)
        .order("sort_order");
      if (error) throw error;
      const seen = new Set<string>();
      const result: string[] = [];
      (data || []).forEach((r: any) => {
        if (r.organisation && !seen.has(r.organisation)) {
          seen.add(r.organisation);
          result.push(r.organisation);
        }
      });
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (orgs.length === 0) return null;

  return (
    <p className="text-xs text-primary-foreground/50 text-center leading-relaxed">
      <span className="uppercase tracking-widest mr-2">Recognised by</span>
      {orgs.join(" · ")}
    </p>
  );
}

/* ── Icon map for dynamic social icons ── */
const ICON_MAP: Record<string, any> = {
  Linkedin, Youtube, Twitter, Facebook, Instagram,
};

/* ── Fallback data ── */
const FALLBACK_TAGLINE = "The world's first mobile network built for business, controlled by businesses.";
const FALLBACK_SOCIAL = [
  { name: "LinkedIn", href: "https://linkedin.com/company/tango-networks", icon: "Linkedin" },
  { name: "YouTube", href: "https://youtube.com/tangonetworks", icon: "Youtube" },
];
const FALLBACK_CTA = [
  { label: "Check out our latest blog", href: "/blog", cta: "READ BLOG" },
  { label: "Read the latest news", href: "/news", cta: "READ NEWS" },
  { label: "Subscribe to our newsletter", href: "/subscribe", cta: "SUBSCRIBE" },
];
const FALLBACK_LEGAL = [
  { name: "Privacy Policy", href: "/privacy" },
  { name: "Terms of Service", href: "/terms" },
  { name: "Cookie Policy", href: "/cookies" },
  { name: "Acceptable Use", href: "/acceptable-use-policy" },
];
const FALLBACK_LINK_GROUPS = [
  { label: "Products", links: [
    { title: "Tango Extend", href: "/products" }, { title: "Enterprise Text Messaging", href: "/solutions/enterprise-text-messaging" },
  ]},
  { label: "Solutions", links: [
    { title: "All Solutions", href: "/solutions/all" },
    { title: "One Voice", href: "/solutions/p/one-voice" }, { title: "Sustainability", href: "/solutions/p/sustainability" },
    { title: "Control", href: "/solutions/p/control" }, { title: "Compliance", href: "/solutions/p/compliance" },
    { title: "Your People", href: "/solutions/p/your-people" }, { title: "Your Customers", href: "/solutions/p/your-customers" },
    { title: "Always On", href: "/solutions/p/always-on" },
    { title: "Work Anywhere", href: "/solutions/p/work-anywhere" },
    { title: "AI & Intelligence", href: "/solutions/p/ai-intelligence" },
  ]},
  { label: "Company", links: [
    { title: "About Us", href: "/about" }, { title: "Leadership", href: "/about-tango-networks/leadership-team" },
    { title: "Partners", href: "/partners" }, { title: "News", href: "/news" },
    { title: "Blog", href: "/blog" }, { title: "Contact Us", href: "/contact" },
  ]},
  { label: "Resources", links: [
    { title: "Resource Library", href: "/resources" }, { title: "Country Availability", href: "/country-availability" },
    { title: "Supported Phones", href: "/supported-phones" }, { title: "FAQs", href: "/faqs" },
    { title: "Value Calculator", href: "/value-calculator" },
  ]},
];

function useFooterSettings() {
  return useQuery({
    queryKey: ["footer_settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("footer_settings").select("key, value");
      if (error) throw error;
      const map: Record<string, any> = {};
      data?.forEach((r: any) => { map[r.key] = r.value; });
      return map;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

function useFooterLinks() {
  return useQuery({
    queryKey: ["footer_links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("footer_links")
        .select("group_label, title, href, sort_order, group_sort_order, is_external")
        .eq("active", true)
        .order("group_sort_order")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function Footer() {
  const { data: settingsMap } = useFooterSettings();
  const { data: rawLinks } = useFooterLinks();

  const tagline = (settingsMap?.tagline as string) || FALLBACK_TAGLINE;
  const logoUrl = (settingsMap?.logo_url as string) || "";
  const accreditationText = (settingsMap?.accreditation_text as string) || "";
  const logoHeight = typeof settingsMap?.logo_height === "number" ? settingsMap.logo_height * 2 : 96;
  const socialLinks: { name: string; href: string; icon: string }[] = Array.isArray(settingsMap?.social_links) ? settingsMap.social_links : FALLBACK_SOCIAL;
  const ctaCards: { label: string; href: string; cta: string }[] = Array.isArray(settingsMap?.cta_cards) ? settingsMap.cta_cards : FALLBACK_CTA;
  const legalLinks: { name: string; href: string }[] = Array.isArray(settingsMap?.legal_links) ? settingsMap.legal_links : FALLBACK_LEGAL;

  const linkGroups = useMemo(() => {
    if (!rawLinks || rawLinks.length === 0) return FALLBACK_LINK_GROUPS;
    const map = new Map<string, { label: string; links: { title: string; href: string; is_external?: boolean }[] }>();
    for (const row of rawLinks) {
      let g = map.get(row.group_label);
      if (!g) { g = { label: row.group_label, links: [] }; map.set(row.group_label, g); }
      g.links.push({ title: row.title, href: row.href, is_external: row.is_external });
    }
    return Array.from(map.values());
  }, [rawLinks]);

  return (
    <footer
      data-build-check="2026-09-11"
      className="bg-primary text-primary-foreground relative overflow-hidden min-h-[700px] md:min-h-[620px] lg:min-h-[500px]"
    >
      {/* Subtle geometric background */}
      <div className="absolute inset-0 opacity-[0.04]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="footer-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="1" fill="white"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#footer-grid)"/>
        </svg>
      </div>

      {/* CTA Section */}
      {ctaCards.length > 0 && (
        <div className="border-b border-primary-foreground/10 relative">
          <div className="container-padding mx-auto py-14">
            <div className="grid gap-8 md:grid-cols-3">
              {ctaCards.map((item) => (
                <div key={item.cta} className="text-center group">
                  <h3 className="text-lg font-semibold mb-3">{item.label}</h3>
                  <Button asChild variant="outline-light">
                    <Link to={item.href}>{item.cta}</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Footer */}
      <div className="container-padding mx-auto py-14 relative">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-6">
          {/* Logo and Contact */}
          <div className="lg:col-span-2">
            <Link
              to="/"
              className="inline-block mb-4"
              style={{ height: `${logoUrl ? logoHeight : 40}px` }}
              aria-label="Tango Networks home"
            >
              <img
                src={logoUrl || tangoLogo}
                alt="Tango Networks"
                width={387}
                height={76}
                style={{ height: `${logoUrl ? logoHeight : 40}px`, width: 'auto', aspectRatio: '387/76' }}
                className={`block max-w-full object-contain${logoUrl ? ' brightness-0 invert' : ''}`}
              />
            </Link>
            <p className="text-sm text-primary-foreground/70 mb-4 leading-relaxed">
              {tagline}
            </p>
            {accreditationText && (
              <p className="text-xs text-primary-foreground/50 mb-6 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                {accreditationText}
              </p>
            )}
            <div className="flex space-x-3">
              {socialLinks.map((item) => {
                const Icon = ICON_MAP[item.icon] || Linkedin;
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg bg-primary-foreground/10 flex items-center justify-center text-primary-foreground/70 hover:bg-primary-foreground/20 hover:!text-primary-foreground transition-all duration-200"
                  >
                    <span className="sr-only">{item.name}</span>
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link Columns */}
          {linkGroups.map((group) => (
            <div key={group.label}>
              <h3 className="text-sm font-bold mb-4 uppercase tracking-wider text-primary-foreground/90">
                {group.label}
              </h3>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.title}>
                    {(link as any).is_external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-foreground/60 hover:!text-accent transition-colors duration-200">
                        {link.title}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm text-primary-foreground/60 hover:!text-accent transition-colors duration-200">
                        {link.title}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10 relative">
        <div className="container-padding mx-auto py-6 space-y-4">
          <RecognisedByLine />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-primary-foreground/60">
              © {new Date().getFullYear()} Tango Networks. All rights reserved.
            </p>
            <div className="flex space-x-6">
              {legalLinks.map((link) => (
                <Link key={link.name} to={link.href} className="text-sm text-primary-foreground/60 hover:!text-accent transition-colors">
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
